import React, { useState, useEffect, useRef } from 'react';
import { DictionaryCategory, fetchDictionaryCategories, addWordToCategory, Word, updateWordStatus } from '../utils/api';
import '../styles/CategorySelector.scss';
import CreateCategoryModal from './CreateCategoryModal';
import logger from '../utils/logger';

interface CategorySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  word: Word;
  onCategorySelected: () => void;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  isOpen,
  onClose,
  word,
  onCategorySelected
}) => {
  const [categories, setCategories] = useState<DictionaryCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false);
  const [showAddTip, setShowAddTip] = useState(false);
  const tipTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
    
    return () => {
      if (tipTimerRef.current) {
        clearTimeout(tipTimerRef.current);
      }
    };
  }, [isOpen]);

  const loadCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchDictionaryCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (err) {
      // Не отображаем ошибку пользователю
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = async (category: DictionaryCategory) => {
    try {
      setIsLoading(true);
      const response = await addWordToCategory(word, category.name);
      if (response.success) {
        logger.log(`Слово "${word.word}" успешно добавлено в категорию "${category.name}"`);
        onCategorySelected();
        onClose();
      } else {
        setError(response.error || 'Не удалось добавить слово в категорию');
      }
    } catch (err) {
      setError('Ошибка при добавлении слова в категорию');
      logger.error('Исключение при добавлении слова в категорию:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWithoutCategory = () => {
    // Проверяем, показывалась ли подсказка ранее
    const tipShown = localStorage.getItem('addWithoutCategoryTipShown');
    
    if (!tipShown) {
      // Показываем подсказку
      setShowAddTip(true);
      
      // Устанавливаем таймер для автоматического скрытия подсказки через 5 секунд
      tipTimerRef.current = window.setTimeout(() => {
        setShowAddTip(false);
        // Добавляем слово после показа подсказки
        processAddWordWithoutCategory();
        // Сохраняем флаг в localStorage
        localStorage.setItem('addWithoutCategoryTipShown', 'true');
      }, 5000);
    } else {
      // Если подсказка уже была показана, просто добавляем слово
      processAddWordWithoutCategory();
    }
  };
  
  const processAddWordWithoutCategory = async () => {
    try {
      setIsLoading(true);
      // Просто устанавливаем статус слова "Учу" без привязки к категории
      const response = await updateWordStatus(word.word, 'IN_PROGRESS');
      if (response.success) {
        logger.log(`Слово "${word.word}" успешно добавлено в список "Учу" без категории`);
        onCategorySelected();
        onClose();
      } else {
        setError(response.error || 'Не удалось добавить слово');
      }
    } catch (err) {
      setError('Ошибка при добавлении слова');
      logger.error('Исключение при добавлении слова:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateCategoryModal = () => {
    setIsCreateCategoryModalOpen(true);
  };

  const closeCreateCategoryModal = () => {
    setIsCreateCategoryModalOpen(false);
  };

  const handleCategoryCreated = async (newCategoryName: string) => {
    try {
      // Сначала загружаем категории, чтобы обновить список
      await loadCategories();
      
      // Затем автоматически добавляем слово в новую категорию
      setIsLoading(true);
      const response = await addWordToCategory(word, newCategoryName);
      if (response.success) {
        logger.log(`Слово "${word.word}" успешно добавлено в новую категорию "${newCategoryName}"`);
        onCategorySelected();
        onClose();
      } else {
        setError(response.error || 'Не удалось добавить слово в новую категорию');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Ошибка при добавлении слова в новую категорию');
      logger.error('Исключение при добавлении слова в новую категорию:', err);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="category-selector-overlay" onClick={onClose}>
      <div className="category-selector" onClick={(e) => e.stopPropagation()}>
        <h2>Выберите категорию</h2>
        <div className="add-category-button-container">
          <button className="add-category-button" onClick={openCreateCategoryModal}>
            <span className="add-icon">+</span>
            <span>Создать категорию</span>
          </button>
        </div>
        <div className="category-selector-content">
          {error ? (
            <div className="error-message">{error}</div>
          ) : categories.length > 0 ? (
            <div className="categories-list">
              {categories.map((category, index) => (
                <div 
                  key={index}
                  className="category-item"
                  onClick={() => handleCategoryClick(category)}
                >
                  <span className="category-emoji">{category.emoji || '📝'}</span>
                  <span className="category-name">{category.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-message">Нет доступных категорий</div>
          )}
        </div>
        
        {showAddTip && (
          <div className="tip-message">
            <span>💡 Добавить слово без категории можно быстрее — двойным касанием по нему</span>
          </div>
        )}
        
        <div className="category-selector-footer">
          <button className="close-button" onClick={onClose}>
            Отмена
          </button>
          <button className="add-without-category-button" onClick={handleAddWithoutCategory}>
            Просто добавить
          </button>
        </div>
      </div>
      <CreateCategoryModal 
        isOpen={isCreateCategoryModalOpen}
        onClose={closeCreateCategoryModal}
        onCategoryCreated={handleCategoryCreated}
      />
    </div>
  );
};

export default CategorySelector; 